"use client";

import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { useSiteSettings } from "@/features/settings/ui/hooks/useSiteSettings";
import { Link } from "@/i18n/navigation";
import { Image } from "@/lib/client/components/Image";
import { useCountryStore } from "@/lib/client/stores/useCountryStore";
import { AspectRatio, Button, cn, Text, VStack } from "@/ui";
import { resolveGiftSetCardImage } from "../../shared/giftSetCardImage";
import type { GiftSet } from "../../shared/types";

interface GiftSetCardProps {
  giftSet: GiftSet;
  index: number;
}

const CARD_IMAGE_CLASS =
  "object-center transition-transform duration-700 ease-out group-hover:scale-105";

function convertedDisplay(
  amountInBase: number,
  exchangeRate: number,
  fractionDigits: number,
): string {
  return Number(amountInBase * exchangeRate).toFixed(fractionDigits);
}

function GiftSetDetailLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <Link
      href={href}
      className="relative block size-full"
    >
      {children}
    </Link>
  );
}

export const GiftSetCard = ({ giftSet, index }: GiftSetCardProps) => {
  const giftT = useTranslations("gift_sets");
  const generalT = useTranslations("general");
  const { country } = useCountryStore();
  const { imageRatio } = useSiteSettings();

  const displayPrice = giftSet.price;
  const hasDiscount = giftSet.membersCombinedPrice > giftSet.price;
  const savingsBase = hasDiscount ? giftSet.membersCombinedPrice - giftSet.price : 0;
  const productCount = giftSet.products.length;
  const detailHref = `/gift-sets/${giftSet.slug}`;
  const cardImage = resolveGiftSetCardImage(giftSet);
  const eager = index < 2;

  return (
    <VStack
      className={cn(
        "group relative min-h-0 flex-1 overflow-hidden rounded-sm border border-border/40 bg-background shadow-sm transition-shadow duration-500 hover:shadow-md",
      )}
      data-testid="gift-set-card"
    >
      <div className="relative">
        <AspectRatio
          className="overflow-hidden bg-muted/30"
          ratio={imageRatio}
        >
          <GiftSetDetailLink href={detailHref}>
            <Image
              src={cardImage.src}
              alt={cardImage.alt || giftSet.displayTitle}
              fill
              quality={72}
              sizes="(max-width: 768px) 50vw, 25vw"
              style={{ objectFit: "cover" }}
              className={CARD_IMAGE_CLASS}
              priority={eager}
              fetchPriority={eager ? "high" : undefined}
            />
          </GiftSetDetailLink>
        </AspectRatio>

        {hasDiscount ? (
          <span className="absolute right-3 top-3 z-10 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.15em] bg-destructive text-destructive-foreground">
            {giftT("save")}{" "}
            <span className="font-price normal-case tracking-normal">
              {country.symbol}
              {convertedDisplay(savingsBase, country.exchangeRate, 0)}
            </span>
          </span>
        ) : null}
      </div>

      <VStack className="relative flex-1 justify-between gap-3 p-4 sm:p-5">
        <VStack className="gap-1">
          <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
            {productCount} {generalT("items_selected")}
          </span>
          <Link
            href={detailHref}
            className="group/title"
          >
            <Text className="font-product-title text-base font-medium text-foreground transition-colors duration-300 group-hover/title:text-primary line-clamp-2">
              {giftSet.displayTitle}
            </Text>
          </Link>
        </VStack>

        <VStack className="gap-3 pt-1">
          <div className="flex items-baseline justify-between gap-2">
            {hasDiscount ? (
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-price text-sm text-muted-foreground line-through">
                  {country.symbol}
                  {convertedDisplay(giftSet.membersCombinedPrice, country.exchangeRate, 2)}
                </span>
                <span className="font-price font-medium text-foreground">
                  {country.symbol}
                  {convertedDisplay(displayPrice, country.exchangeRate, 2)}
                </span>
              </div>
            ) : (
              <span className="font-price font-medium text-foreground">
                {country.symbol}
                {convertedDisplay(displayPrice, country.exchangeRate, 2)}
              </span>
            )}
          </div>

          <Button
            asChild
            className="w-full bg-foreground text-background rounded-none py-5 text-[11px] font-medium uppercase tracking-[0.15em] hover:bg-primary hover:text-primary-foreground hover:shadow-gold transition-all duration-500"
          >
            <Link
              href={detailHref}
              className="inline-flex items-center justify-center"
            >
              {giftT("cta_view_set")}
            </Link>
          </Button>
        </VStack>
      </VStack>
    </VStack>
  );
};
